import React, { useEffect, useState, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import siteAnalyticsService, { RealtimeResponse } from '../../services/siteAnalytics.service';
import { fa, fmtTime, decodePath } from './useDateRange';
import { PALETTE } from './chartConfig';
import './analytics.css';

const REFRESH_MS = 10_000;

const SiteAnalyticsRealtime: React.FC = () => {
  const [data, setData] = useState<RealtimeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const fetchOnce = async () => {
    if (inFlightRef.current) return; // درخواست قبلی هنوز تمام نشده — این tick رد می‌شود
    inFlightRef.current = true;
    try {
      setErr(null);
      const r = await siteAnalyticsService.getRealtime();
      setData(r);
      setLastUpdated(new Date());
    } catch (e: any) {
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchOnce();
    // تب مخفی → polling متوقف؛ بازگشت به تب → فوراً یک fetch تازه
    const tick = () => {
      if (document.hidden) return;
      fetchOnce();
    };
    const onVisibility = () => {
      if (!document.hidden) fetchOnce();
    };
    timerRef.current = window.setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTopList = (
    title: string,
    rows: Array<{ key: string; count: number }> | undefined,
    color = PALETTE.primary
  ) => {
    const max = rows && rows.length > 0 ? rows[0].count : 0;
    return (
      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>{title}</h3>
        </div>
        {!rows || rows.length === 0 ? (
          <div className="analytics-empty">داده‌ای نیست</div>
        ) : (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>مقدار</th>
                <th style={{ width: 80 }}>تعداد</th>
                <th>سهم</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 8).map((r) => {
                const total = rows.reduce((s, x) => s + x.count, 0);
                return (
                  <tr key={r.key}>
                    <td>{r.key || '(نامشخص)'}</td>
                    <td>{fa(r.count)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span
                        className="pct-bar"
                        style={{ width: `${max ? (r.count / max) * 100 : 0}px`, background: color }}
                      />
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 6 }}>
                        {total ? fa(Math.round((r.count / total) * 100)) : '۰'}٪
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const lastHoursBars = data?.last24Hours
    ? {
        // برچسب‌ها با تایم‌زون تهران — بک‌اند باکت‌ها را تهرانی می‌سازد؛ بدون این،
        // ادمینی با TZ متفاوت «ساعت پیک» را جابه‌جا می‌دید
        labels: data.last24Hours.map((h) =>
          new Date(h.hour).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tehran' })
        ),
        datasets: [
          {
            label: 'سشن',
            data: data.last24Hours.map((h) => h.sessions),
            backgroundColor: PALETTE.primary,
          },
          {
            label: 'بازدید صفحه',
            data: data.last24Hours.map((h) => h.pageViews),
            backgroundColor: PALETTE.warning,
          },
        ],
      }
    : null;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>
          بازدیدکنندگان آنلاین
          <span className="analytics-pulse" />
        </h2>
        <div className="analytics-controls">
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            به‌روزرسانی خودکار هر ۱۰ ثانیه
            {lastUpdated && ` · آخرین: ${lastUpdated.toLocaleTimeString('fa-IR')}`}
          </span>
          <button onClick={fetchOnce} disabled={loading}>
            رفرش
          </button>
        </div>
      </div>

      {err && <div className="analytics-empty" style={{ color: 'var(--color-error-dark)' }}>{err}</div>}

      {data && (
        <>
          <div className="analytics-kpi-grid">
            <div className="analytics-kpi-card green">
              <div className="accent" />
              <div className="label">کاربران آنلاین (۵ دقیقه‌ی اخیر)</div>
              <div className="value" style={{ fontSize: 36 }}>
                {fa(data.activeNow.visitors)}
              </div>
              <span className="change flat">{fa(data.activeNow.sessions)} سشن فعال</span>
            </div>
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">فعال در ۳۰ دقیقه‌ی اخیر</div>
              <div className="value">{fa(data.activeLast30Min)}</div>
            </div>
            {/* 🆕 نبض تجاری زنده */}
            <div className="analytics-kpi-card orange">
              <div className="accent" />
              <div className="label">خرید در ۳۰ دقیقه‌ی اخیر</div>
              <div className="value">{fa(data.last30Min?.purchases || 0)}</div>
              <span className="change flat">
                {data.last30Min?.revenue ? `${fa(data.last30Min.revenue)} تومان` : '—'}
              </span>
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>سشن‌ها در ۲۴ ساعت اخیر</h3>
            </div>
            <div className="analytics-chart-container">
              {lastHoursBars ? (
                <Bar
                  data={lastHoursBars}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'top' as const, rtl: true, textDirection: 'rtl' },
                      tooltip: { rtl: true, textDirection: 'rtl' },
                    },
                    scales: { y: { beginAtZero: true } },
                  }}
                />
              ) : (
                <div className="analytics-empty">داده‌ای نیست</div>
              )}
            </div>
          </div>

          <div className="analytics-grid-2col">
            {renderTopList('بر اساس دستگاه', data.byDevice, PALETTE.purple)}
            {renderTopList('بر اساس کشور', data.byCountry, PALETTE.success)}
          </div>

          <div className="analytics-grid-2col">
            {renderTopList('بر اساس منبع', data.byMedium, PALETTE.warning)}
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>صفحات فعال الان</h3>
              </div>
              {data.topActivePages.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>مسیر</th>
                      <th style={{ width: 80 }}>کاربر فعال</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topActivePages.map((p) => (
                      <tr key={p.path}>
                        <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(p.path)}</td>
                        <td>
                          <strong>{fa(p.activeVisitors)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>آخرین بازدیدها (۳۰ دقیقه‌ی اخیر — حداکثر ۵۰ مورد)</h3>
            </div>
            {data.recentEvents.length === 0 ? (
              <div className="analytics-empty">بازدید جدیدی نیست</div>
            ) : (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>زمان</th>
                    <th>مسیر</th>
                    <th>دستگاه</th>
                    <th>کشور</th>
                    <th>منبع</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentEvents.map((e, i) => (
                    <tr key={i}>
                      <td>{fmtTime(e.ts)}</td>
                      <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(e.path)}</td>
                      <td>{e.deviceType || '-'}</td>
                      <td>{e.country || '-'}</td>
                      <td>{e.medium || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {data.recentActivity && data.recentActivity.length > 0 && (
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>آخرین رویدادها (۳۰ دقیقه‌ی اخیر)</h3>
              </div>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>زمان</th>
                    <th>رویداد</th>
                    <th>دسته</th>
                    <th>مسیر</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentActivity.map((e, i) => (
                    <tr key={i}>
                      <td>{fmtTime(e.ts)}</td>
                      <td><strong>{e.name}</strong></td>
                      <td>{e.category || '-'}</td>
                      <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(e.path || '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SiteAnalyticsRealtime;
